import { inject, Injectable } from '@angular/core';
import type { Locator } from '@readium/shared';
import { createReadium2Locator, locatorSafeSerialize } from '../../helpers/locator';
import { getLocatorContextAndReadingPoint } from '../../helpers/readium';
import type {
  LocatorLike,
  MetadataUserDataSavedPlaceResponse,
} from '../../models/metadata-response';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';
import { BookAnalysisService } from './book-analysis';

@Injectable({
  providedIn: 'root',
})
export class ReaderLocationService {
  private readonly metadataService = inject(MetadataService);
  private readonly bookAnalysisService = inject(BookAnalysisService);
  private readonly readerApi = inject(ReaderApi);

  getLastLocation(savedPlace: MetadataUserDataSavedPlaceResponse | undefined): Locator | undefined {
    if (!savedPlace) return undefined;

    try {
      return savedPlace.locator
        ? createReadium2Locator(savedPlace.locator, this.metadataService.positions())
        : (this.bookAnalysisService.getLocatorForPercentage(
            savedPlace.progress,
            savedPlace.contextReadingPoint,
          ) ?? undefined);
    } catch {
      return undefined;
    }
  }

  saveLastLocation(locator: Locator | undefined, frames: HTMLIFrameElement[]): void {
    if (!locator) return;
    const { text, contextReadingPoint } = getLocatorContextAndReadingPoint(frames);

    const params = {
      locator: { ...locatorSafeSerialize(locator), text } as LocatorLike,
      idref: this.metadataService.componentIdFor(locator.href),
      contextReadingPoint,
      // Readium copies the chapter start into totalProgression; the page's own % is needed here.
      progress: this.bookAnalysisService.calculateProgressFromLocator(locator),
    };

    this.readerApi.saveLastLocation(params);
  }
}
